import React, {useEffect, useRef, useState, useCallback} from 'react';
import {Box, Button, Stack, Typography, useMediaQuery, Alert} from '@mui/material';
import ScienceIcon from '@mui/icons-material/Science';
import RefreshIcon from '@mui/icons-material/Refresh';
import BiotechIcon from '@mui/icons-material/Biotech';
import MedicalInformationIcon from '@mui/icons-material/MedicalInformation';
import HealthAndSafetyIcon from '@mui/icons-material/HealthAndSafety';
import mainLogo from '../../images/black_letters_with_blue_shadow.svg';
import {getClientConfig} from '../../config/clientThemes';
import HealthCategoryNav from '../../components/NavigationBar/HealthCategoryNavigationBar';
import {identifyClientType} from '../../utils/identifyClientType';
import {CLIENT_TYPES} from '../../api/clientconfig';
import ShareButtons from '../../components/buttons/ShareButtons';
import {useClientApi} from '../../api/client';

const ShowLabTestsPage = ({
  blurAndHide,
  handleNavigation,
  onBackBtnClick,
  getCategories,
  activeCategoryIndex,
  handleCategorySelect,
  item,
}) => {
  const cardRef = useRef(null);
  const [ShadowClass, setShadowClass] = useState(true);
  const isSmallScreen = useMediaQuery('(max-width:499px)');
  const isGalaxyFold = useMediaQuery('(max-width:280px)');
  const [clientType, setClientType] = useState(CLIENT_TYPES.HELLO_HEALTH);
  const isLargeScreen = useMediaQuery(theme => theme.breakpoints.up('lg'));
  const config = getClientConfig(clientType);

  // State for lab tests from API
  const [labTests, setLabTests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [hasInitialized, setHasInitialized] = useState(false);

  // Get API functions
  const {getLabTestRecommendations} = useClientApi(CLIENT_TYPES.HELLO_HEALTH);

  // Initialize client type once
  useEffect(() => {
    const type = identifyClientType();
    setClientType(type);
  }, []);

  // Memoize the fetch function to prevent unnecessary re-renders
  const fetchLabTestRecommendations = useCallback(async () => {
    if (!getLabTestRecommendations) {
      console.log('API function not available yet');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      // Get assessment ID from localStorage and convert to integer
      const assessmentIdString = localStorage.getItem('hellohealth-assessment-id');
      const assessmentId = parseInt(assessmentIdString, 10);

      console.log('Assessment ID from localStorage:', assessmentIdString);
      console.log('Parsed Assessment ID:', assessmentId);

      if (!assessmentId || isNaN(assessmentId)) {
        throw new Error('Assessment ID not found. Please complete the health assessment first.');
      }

      console.log('Calling API with assessment ID:', assessmentId);
      const response = await getLabTestRecommendations(assessmentId);
      console.log('API Response:', response);

      // Handle response data
      const testsData = response?.data || response;

      if (!Array.isArray(testsData)) {
        console.error('Invalid response format:', testsData);
        throw new Error('Invalid response format');
      }

      // Transform API response and filter out tests with null links
      const transformedTests = testsData
        .filter(test => test.lab_test_link && test.lab_test_link !== null) // Filter out tests with null links
        .map((test, index) => ({
          id: test.id || `test-${index}`,
          assessment_id: test.assessment_id,
          name: test.recommended_test || 'Unknown Test',
          description: test.reason || 'No description available',
          link: test.lab_test_link,
          image: test.lab_test_image, // Keep image even if null, we'll handle it in rendering
        }));

      console.log('Transformed tests data:', testsData);
      console.log('Filtered and transformed tests:', transformedTests);
      setLabTests(transformedTests);
    } catch (err) {
      console.error('Error fetching lab tests:', err);
      setError(err.message || 'Failed to fetch lab test recommendations');
    } finally {
      setLoading(false);
      setHasInitialized(true);
    }
  }, [getLabTestRecommendations]);

  // Only fetch once when component mounts and API is available
  useEffect(() => {
    if (getLabTestRecommendations && !hasInitialized) {
      console.log('Initializing lab test fetch...');
      fetchLabTestRecommendations();
    }
  }, [getLabTestRecommendations, hasInitialized, fetchLabTestRecommendations]);

  const cssToBlurAndHideCard = {
    filter: blurAndHide ? 'blur(10px)' : 'blur(0px)',
    pointerEvents: blurAndHide ? 'none' : 'auto',
  };

  const handleRedirect = testId => {
    console.log(`View more information about test ID: ${testId}`);
    alert('Test details would open here. This functionality will be connected to an API in the future.');
  };

  const handleRetry = () => {
    setHasInitialized(false);
    fetchLabTestRecommendations();
  };

  // Debug logging
  console.log('Component render - Loading:', loading, 'Error:', error, 'Lab Tests Count:', labTests.length);

  // Loading state
  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          height: '200px',
          gap: 2,
        }}
      >
        <Typography>Loading lab test recommendations...</Typography>
      </Box>
    );
  }

  // Error state
  if (error) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '300px',
          textAlign: 'center',
          padding: '20px',
          gap: 2,
        }}
      >
        <Alert severity="error" sx={{mb: 2, maxWidth: '500px'}}>
          {error}
        </Alert>
        <Button onClick={handleRetry} variant="contained" startIcon={<RefreshIcon />}>
          Try Again
        </Button>
      </Box>
    );
  }

  // Empty state component
  const EmptyState = () => (
    <Box sx={{height: '150px', textAlign: 'center', padding: '20px 0px'}}>
      <Typography variant="body1" gutterBottom>
        No Lab Test Recommendations Found
      </Typography>
    </Box>
  );

  // Main render - Show up to 3 tests, but show whatever is available if less than 3
  const renderLabTests = () => {
    if (labTests.length === 0) {
      return <EmptyState />;
    }

    // Show up to 3 lab tests, or whatever is available
    const testsToShow = labTests.slice(0, 3);

    // Array of icons to cycle through
    const testIcons = [BiotechIcon, MedicalInformationIcon, HealthAndSafetyIcon];

    return testsToShow.map((test, index) => {
      // Get the corresponding icon (cycles through the 3 icons)
      const IconComponent = testIcons[index % testIcons.length];

      return (
        <Box
          key={test.id}
          sx={{
            borderRadius: '15px',
            padding: isSmallScreen ? '1px 12px 2px 12px' : '2px 14px', // Reduced bottom padding
            marginBottom: index < testsToShow.length - 1 ? (isSmallScreen ? '2px' : '4px') : '0px', // Add small margin between tests, none for last
            ...cssToBlurAndHideCard,
          }}
        >
          <Stack display="flex" flexDirection="row" justifyContent="space-between" gap={1}>
            <Stack flex="10">
              <Typography
                sx={{
                  fontSize: isSmallScreen ? '13px' : '14px',
                  fontWeight: 600,
                  color: '#000000',
                  display: '-webkit-box',
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden',
                  WebkitLineClamp: 1,
                  marginBottom: isSmallScreen ? '1px' : '2px', // Reduced margin
                }}
              >
                {test.name}
              </Typography>
              <Typography
                sx={{
                  fontSize: isSmallScreen ? '11px' : '14px',
                  fontWeight: 500,
                  color: '#000000',
                  display: '-webkit-box',
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden',
                  lineHeight: isSmallScreen ? 1.1 : 1.2, // Tighter line height
                  WebkitLineClamp: 2,
                  marginBottom: isSmallScreen ? '2px' : '3px', // Reduced margin
                }}
              >
                {test.description}
              </Typography>
              <Button
                onClick={() => {
                  const link = document.createElement('a');
                  link.href = test.link;
                  link.target = '_blank';
                  link.rel = 'noopener noreferrer';
                  link.click();
                }}
                sx={{
                  color: clientType === CLIENT_TYPES.DEFAULT ? '#72D8DA' : '#F15C30',
                  fontSize: isSmallScreen ? '12px' : '15px',
                  display: 'flex',
                  justifyContent: 'start',
                  padding: '2px 4px 2px 0px', // Reduced vertical padding
                  minHeight: 'auto', // Allow button to be smaller
                  '&:hover': {
                    backgroundColor: 'none',
                    background: 'transparent',
                  },
                }}
              >
                <Typography
                  sx={{
                    fontSize: '20px',
                    backgroundColor: clientType === CLIENT_TYPES.DEFAULT ? '#72D8DA' : '#F15C30',
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    marginRight: '6px',
                    width: isSmallScreen ? '20px' : '21px', // Slightly smaller on small screens
                    height: isSmallScreen ? '20px' : '21px',
                    '&:hover': {
                      backgroundColor: 'transparent',
                    },
                  }}
                >
                  <ScienceIcon sx={{color: 'white', fontSize: isSmallScreen ? '12px' : '16px'}} />
                </Typography>
                Learn More
              </Button>
            </Stack>

            {/* Right side icon or image */}
            <Stack flex="1" display="flex" alignItems="center" justifyContent="center">
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  minHeight: isSmallScreen ? '50px' : '60px', // Ensure consistent height
                }}
              >
                {/* Show image if available, otherwise show icon */}
                {test.image && test.image !== null ? (
                  <img
                    src={test.image}
                    alt=""
                    width={isSmallScreen ? 55 : 70}
                    height={isSmallScreen ? 55 : 70}
                    style={{objectFit: 'contain'}}
                  />
                ) : (
                  <IconComponent
                    sx={{
                      color: clientType === CLIENT_TYPES.DEFAULT ? '#72D8DA' : '#F15C30',
                      fontSize: isSmallScreen ? '28px' : '38px', // Slightly smaller icons
                    }}
                  />
                )}
              </Box>
            </Stack>
          </Stack>
        </Box>
      );
    });
  };

  return isLargeScreen ? (
    <Stack className="result-page-card" padding={3}>
      <Box
        sx={{
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          width: '100%',
        }}
        className="results-page-wrapper"
      >
        <Stack className="result-page-card large-card" padding={isGalaxyFold ? 1 : 2}>
          <Stack className="large-result-component">
            <Stack
              sx={{
                minHeight: '380px !important',
              }}
              className={ShadowClass ? 'result-component card-gradient large' : 'result-component large'}
            >
              {renderLabTests()}
              <Box className="result-logo" sx={{marginBottom: '0px', paddingTop: '4px'}}>
                <img src={config.logos.main} height={config.logos.height} className="" alt="Logo" />
                {clientType !== CLIENT_TYPES.DEFAULT && (
                  <Typography sx={{fontSize: '14px'}}>
                    Your health scan was powered by
                    <img src={mainLogo} className="custom-main-logo-for-md" alt="Health scan powered by" />
                  </Typography>
                )}
              </Box>
            </Stack>
          </Stack>
        </Stack>
      </Box>
    </Stack>
  ) : (
    <Box
      sx={{
        backgroundColor: '#FFF',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        width: '90%',
        padding: isLargeScreen ? '20px' : isSmallScreen ? '4px 20px' : '10px 20px',
      }}
      className="results-page-wrapper"
    >
      <Stack className="result-page-card" sx={{backgroundColor: '#fff'}}>
        <Box style={{textDecoration: 'none', width: '100%', display: 'flex', justifyContent: 'space-between'}}>
          <Stack onClick={onBackBtnClick} display="flex" flexDirection="row" style={{cursor: 'pointer'}}>
            {config?.backButton}
          </Stack>
        </Box>
        <Stack>
          <Stack>
            <Typography
              fontSize={isSmallScreen ? '14' : '18px'}
              fontWeight="700"
              color="#545454"
              textAlign="center"
              marginTop={isSmallScreen ? '2px' : '4px'}
              lineHeight={isSmallScreen ? '16px' : '30px'}
              marginBottom="4px"
            >
              Lab Tests For You
            </Typography>
            <Typography
              sx={{
                color: '#545454',
                fontFamily: 'Poppins',
                fontSize: isSmallScreen ? '12px' : '14px',
                fontWeight: 600,
                lineHeight: isSmallScreen ? 1.2 : 1.5,
                textAlign: 'center',
              }}
            >
              Based on your health assessment, these lab tests could provide valuable insights.
            </Typography>
          </Stack>
          <Stack
            ref={cardRef}
            sx={{position: 'relative', bottom: '25px'}}
            className={`${ShadowClass ? 'result-component card-gradient' : 'result-component'} result-component-org`}
          >
            {renderLabTests()}
            <Box className="result-logo" sx={{bottom: '6px !important', paddingTop: '4px !important'}}>
              <img src={config.logos.main} height={config.logos.height} className="" alt="Logo" />
              {clientType !== CLIENT_TYPES.DEFAULT && (
                <Typography
                  sx={{
                    fontSize: isGalaxyFold ? '8px' : '10px',
                    display: 'flex',
                    alignItems: 'center',
                    width: '100%',
                    paddingTop: '8px',
                    justifyContent: 'center',
                  }}
                >
                  Your health scan was powered by
                  <img src={mainLogo} className="custom-main-logo" alt="Health scan powered by" />
                </Typography>
              )}
            </Box>
          </Stack>
        </Stack>
        <Box sx={{position: 'relative', bottom: '25px'}}>
          <HealthCategoryNav
            categories={getCategories}
            activeIndex={activeCategoryIndex}
            onSelectCategory={handleCategorySelect}
            isLargeScreen={false}
            clientBaseNavCss={{
              arrowColor: config?.resultPageSlider?.arrowColor,
              arrowHOverColor: config?.resultPageSlider?.arrowHOverColor,
              movingIndicatorColor: config?.resultPageSlider?.movingIndicatorColor,
              startIconColorActive: config?.resultPageSlider?.startIconColorActive,
            }}
          />
        </Box>

        <Box>
          <>
            <Button
              fullWidth
              onClick={() => {
                window.open('https://www.gethellohealth.com/collections/lab-tests-1', '_blank');
              }}
              className={'btn-shadow'}
              sx={{
                height: isSmallScreen ? '50px' : '60px',
                borderRadius: '10px',
                marginBottom: isSmallScreen ? '6px' : '10px',
                background: config.button.background,
                maxWidth: '400px',
                textAlign: 'center',
                color: '#fff',
                '&:hover': {
                  color: 'black',
                },
              }}
            >
              <Typography
                className="btn-txt"
                variant="p"
                component="p"
                fontWeight="600"
                sx={{
                  fontSize: isSmallScreen ? '16px' : '20px',
                }}
              >
                See All Tests
              </Typography>
            </Button>

            <ShareButtons socialButtonsCss={config?.socialButtons} cardRef={cardRef} />
          </>
        </Box>
      </Stack>
    </Box>
  );
};

export default ShowLabTestsPage;
